import { useState, FormEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import type React from "react";

interface FieldErrors {
  name?: string;
  email?: string;
  service?: string;
  message?: string;
}

export default function ContactSection() {
  const { t, i18n } = useTranslation();
  const isJapanese = i18n.language === "ja";
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation();

  const validate = (form: HTMLFormElement, count: number): FieldErrors => {
    const errs: FieldErrors = {};
    const name = (form.elements.namedItem("name") as HTMLInputElement)?.value.trim();
    const email = (form.elements.namedItem("email") as HTMLInputElement)?.value.trim();
    const service = (form.elements.namedItem("service") as HTMLSelectElement)?.value;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value.trim();

    if (!name) errs.name = t("contact_err_name");
    if (!email) {
      errs.email = t("contact_err_email_required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = t("contact_err_email_invalid");
    }
    if (!service) errs.service = t("contact_err_service");
    if (!message) {
      errs.message = t("contact_err_message_required");
    } else if (count > 500) {
      errs.message = t("contact_err_message_limit");
    }
    return errs;
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (formRef.current) {
      setErrors(validate(formRef.current, charCount));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const errs = validate(form, charCount);
    setErrors(errs);
    setTouched({ name: true, email: true, service: true, message: true });
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    setSendError(false);

    const formData = new FormData(form);
    const payload: Record<string, string> = {};
    formData.forEach((value, key) => {
      payload[key] = value.toString();
    });

    try {
      const res = await fetch("https://formspree.io/f/xgorkaky", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const json = await res.json().catch(() => ({}));
        console.error("Formspree error:", json);
        setSendError(true);
      }
    } catch (err) {
      console.error("Network error:", err);
      setSendError(true);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field: keyof FieldErrors) =>
    `w-full border rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none transition-colors ${
      touched[field] && errors[field]
        ? "border-red-300 focus:border-red-400 bg-red-50/30"
        : "border-gray-200 focus:border-[#0ABAB5]"
    }`;

  const contactInfo = [
    { icon: "ri-map-pin-line", labelKey: "contact_address_label", valueKey: "contact_address_value" },
    { icon: "ri-mail-line", labelKey: "contact_email_label", value: "tenten.sensei.okinawa@gmail.com" },
    { icon: "ri-time-line", labelKey: "contact_hours_label", valueKey: "contact_hours_value" },
  ];

  return (
    <section id="contact" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row gap-10 sm:gap-16">

          {/* Left Info */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`w-full lg:w-5/12 transition-all ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
            style={{ transitionDuration: "800ms" }}
          >
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              {t("contact_heading")}
            </h2>
            <div className="w-12 h-0.5 bg-[#0ABAB5] mb-8" />
            <p className="text-gray-500 text-base leading-relaxed mb-10">{t("contact_desc")}</p>

            <div className="space-y-6">
              {contactInfo.map((item) => (
                <div key={item.labelKey} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0ABAB5]/10 flex-shrink-0">
                    <i className={`${item.icon} text-[#0ABAB5] text-lg`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-0.5">{t(item.labelKey)}</p>
                    <p className="text-gray-700 text-sm">
                      {item.value ?? t(item.valueKey as string)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 bg-white rounded-2xl border border-gray-100 px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#e8f9f8] flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-[#0ABAB5] text-sm" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">{t("contact_note_title")}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {t("contact_note_desc").split("\n").map((line, i, arr) => (
                      <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`w-full lg:w-7/12 transition-all ease-out ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
            style={{ transitionDuration: "800ms", transitionDelay: rightVisible ? "150ms" : "0ms" }}
          >
            {submitted ? (
              <div className="bg-white rounded-3xl p-8 sm:p-10 md:p-14 flex flex-col items-center justify-center text-center min-h-[420px] sm:min-h-[520px]">
                <div className="relative mb-8">
                  <div className="w-24 h-24 flex items-center justify-center rounded-full bg-[#e8f9f8]">
                    <i className="ri-check-line text-[#0ABAB5] text-5xl" />
                  </div>
                  <div className="absolute inset-0 rounded-full border-2 border-[#0ABAB5]/30 animate-ping" />
                </div>
                <p className="text-xs text-[#0ABAB5] font-bold tracking-widest mb-3">THANK YOU</p>
                <h3
                  className="text-2xl md:text-3xl font-bold text-gray-800 mb-4"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  {t("contact_thanks_title")}
                </h3>
                <p
                  className="text-gray-500 text-sm leading-loose mb-8 max-w-sm"
                  dangerouslySetInnerHTML={{ __html: t("contact_thanks_desc").replace(/\n/g, "<br />") }}
                />
                <div className="w-full max-w-xs bg-[#f0faf9] rounded-2xl px-6 py-5 text-left space-y-3 mb-8">
                  {[
                    { icon: "ri-mail-check-line", key: "contact_thanks_check1" },
                    { icon: "ri-spam-2-line", key: "contact_thanks_check2" },
                    { icon: "ri-time-line", key: "contact_thanks_check3" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white flex-shrink-0">
                        <i className={`${item.icon} text-[#0ABAB5] text-sm`} />
                      </div>
                      <p className="text-xs text-gray-600">{t(item.key)}</p>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setErrors({});
                    setTouched({});
                    setCharCount(0);
                  }}
                  className="text-xs text-gray-400 hover:text-[#0ABAB5] transition-colors cursor-pointer underline underline-offset-2"
                >
                  {t("contact_thanks_another")}
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                noValidate
                className="bg-white rounded-3xl p-6 sm:p-8 md:p-10"
              >
                {sendError && (
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6">
                    <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-600 leading-relaxed">{t("contact_error")}</p>
                  </div>
                )}

                {/* 名前 + フリガナ/電話 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      {t("contact_field_name")} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder={t("contact_placeholder_name")}
                      onBlur={() => handleBlur("name")}
                      onChange={() => {
                        if (touched.name && formRef.current) setErrors(validate(formRef.current, charCount));
                      }}
                      className={inputClass("name")}
                    />
                    {touched.name && errors.name && (
                      <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1">
                        <i className="ri-error-warning-line" />{errors.name}
                      </p>
                    )}
                  </div>
                  {isJapanese ? (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-2">
                        {t("contact_field_furigana")}
                      </label>
                      <input
                        type="text"
                        name="furigana"
                        placeholder={t("contact_placeholder_furigana")}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#0ABAB5] transition-colors"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-2">
                        {t("contact_field_phone")}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder={t("contact_placeholder_phone")}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#0ABAB5] transition-colors"
                      />
                    </div>
                  )}
                </div>

                {/* メール + 電話/サービス */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      {t("contact_field_email")} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder={t("contact_placeholder_email")}
                      onBlur={() => handleBlur("email")}
                      onChange={() => {
                        if (touched.email && formRef.current) setErrors(validate(formRef.current, charCount));
                      }}
                      className={inputClass("email")}
                    />
                    {touched.email && errors.email && (
                      <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1">
                        <i className="ri-error-warning-line" />{errors.email}
                      </p>
                    )}
                  </div>
                  {isJapanese ? (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-2">
                        {t("contact_field_phone")}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder={t("contact_placeholder_phone")}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#0ABAB5] transition-colors"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-2">
                        {t("contact_field_service")} <span className="text-red-400">*</span>
                      </label>
                      <select
                        name="service"
                        onBlur={() => handleBlur("service")}
                        onChange={() => {
                          if (touched.service && formRef.current) setErrors(validate(formRef.current, charCount));
                        }}
                        className={`${inputClass("service")} bg-white cursor-pointer`}
                      >
                        <option value="">{t("contact_select_placeholder")}</option>
                        <option value={t("contact_service1")}>{t("contact_service1")}</option>
                        <option value={t("contact_service2")}>{t("contact_service2")}</option>
                        <option value={t("contact_service3")}>{t("contact_service3")}</option>
                        <option value={t("contact_service4")}>{t("contact_service4")}</option>
                      </select>
                      {touched.service && errors.service && (
                        <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1">
                          <i className="ri-error-warning-line" />{errors.service}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* 日本語のみ: サービス選択 */}
                {isJapanese && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      {t("contact_field_service")} <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="service"
                      onBlur={() => handleBlur("service")}
                      onChange={() => {
                        if (touched.service && formRef.current) setErrors(validate(formRef.current, charCount));
                      }}
                      className={`${inputClass("service")} bg-white cursor-pointer`}
                    >
                      <option value="">{t("contact_select_placeholder")}</option>
                      <option value={t("contact_service1")}>{t("contact_service1")}</option>
                      <option value={t("contact_service2")}>{t("contact_service2")}</option>
                      <option value={t("contact_service3")}>{t("contact_service3")}</option>
                      <option value={t("contact_service4")}>{t("contact_service4")}</option>
                    </select>
                    {touched.service && errors.service && (
                      <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1">
                        <i className="ri-error-warning-line" />{errors.service}
                      </p>
                    )}
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-xs font-semibold text-gray-600 mb-2">
                    {t("contact_field_message")} <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder={t("contact_placeholder_message")}
                    onBlur={() => handleBlur("message")}
                    onChange={(e) => {
                      setCharCount(e.target.value.length);
                      if (touched.message && formRef.current) setErrors(validate(formRef.current, e.target.value.length));
                    }}
                    className={`${inputClass("message")} resize-none`}
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <div>
                      {touched.message && errors.message && (
                        <p className="text-xs text-red-400 flex items-center gap-1">
                          <i className="ri-error-warning-line" />{errors.message}
                        </p>
                      )}
                    </div>
                    <p className={`text-xs ml-auto ${charCount > 500 ? "text-red-400 font-semibold" : charCount > 400 ? "text-amber-400" : "text-gray-400"}`}>
                      {charCount}/500
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full bg-[#0ABAB5] text-white font-semibold py-4 rounded-full hover:bg-[#089e99] transition-all duration-200 text-sm disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      {t("contact_submitting")}
                    </>
                  ) : (
                    <>
                      {t("contact_submit")}
                      <i className="ri-send-plane-line" />
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-gray-400 mt-4">{t("contact_privacy")}</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
